import {
  ErrorCode,
  FulfillFailure,
  FulfillResponse,
  FulfillResponseEnum,
  FulfillResponseV0,
  FulfillSuccessV0,
  Payment,
  PaymentError,
  ProductError,
  PurchaseError,
  PurchaseErrorEnum,
} from "@b2bportal/purchase-api";
import {
  COMPLETE_BUY_HOTEL,
  EffectiveRangeEnum,
  HotelCancellationPolicyV2,
  HotelCancellationPolicyV2Enum,
  Reservation,
} from "redmond";
import { delay, putResolve, select } from "redux-saga/effects";
import {
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
} from "@capone/common";
import { brexBridge, getIsBrexApp } from "b2b-base/src/brex/bridge";
import { buildHotelBookingData } from "../../utils/buildHotelBookingData";
import dayjs from "dayjs";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { IStoreState } from "../../../../reducers/types";
import {
  getHotelCfarPremiumProperties,
  getHotelRefundableRatePremiumSelector,
} from "../../../shop/reducer";
import {
  IPollConfirmationDetails,
  setConfirmationDetails,
  setPollConfirmationDetailsCallStateFailure,
  setPollConfirmationDetailsCallStateSuccess,
} from "../../actions/actions";
import {
  getCompleteBuyHotelProperties,
  getSession,
  getTripPurpose,
} from "../../reducer";
import { getPurchaseApis } from "../../utils";

const getCardDeclineProps = (errors: PurchaseError[]) => {
  const cardError = errors.find(
    (e) => e.Error === PurchaseErrorEnum.PaymentError && (e as PaymentError).value.type === Payment.Card
  );
  if (!cardError) return {};
  const { messageTitle, messageBody } = (cardError as PaymentError).value.value;
  return messageTitle && messageBody
    ? { card_decline_reason_title: messageTitle, card_decline_reason_body: messageBody }
    : {};
};

export const toErrorString = (error: PurchaseError): string => {
  switch (error.Error) {
    case PurchaseErrorEnum.ErrorCode: {
      const e = error as ErrorCode;
      return `${e.code}${e.message ? ` - ${e.message}` : ""}`;
    }
    case PurchaseErrorEnum.PaymentError: {
      const paymentError = error as PaymentError;
      return JSON.stringify(paymentError.value.value);
    }
    case PurchaseErrorEnum.ProductError: {
      const productError = error as ProductError;
      return JSON.stringify(productError.value.value);
    }
    default:
      return PurchaseErrorEnum[error.Error];
  }
};

const getFreeCancellationDeadline = (
  cancellationPolicy: HotelCancellationPolicyV2
): string | undefined => {
  if (
    cancellationPolicy.CancellationPolicyV2 !==
    HotelCancellationPolicyV2Enum.FullyRefundable
  ) {
    return undefined;
  }
  const { effectiveRange } = cancellationPolicy;
  return effectiveRange.EffectiveRange === EffectiveRangeEnum.Available
    ? effectiveRange.startTime
    : undefined;
};

export function* pollConfirmationDetailsSaga({
  agentFee,
  isRecommended,
  initialTime,
}: IPollConfirmationDetails) {
  try {
    const state: IStoreState = yield select();
    const sessionToken = getSession(state);
    const tripPurpose = getTripPurpose(state);
    const { pollFinalized } = getPurchaseApis();

    if (!sessionToken) {
      throw new Error("Session token is not present.");
    }

    const delayTimes = [1000];
    let pollFailed = false;
    let index = 0;

    while (!pollFailed) {
      yield delay(delayTimes[index]);
      const finalizedCheckedResponse: FulfillResponse | FulfillResponseV0 =
        yield pollFinalized({
          req: sessionToken,
        }).catch((e) => {
          // [CMKT-1150] do not error out on network errors, instead retry
          if (e.response.status !== 200) {
            return Promise.resolve({
              data: { result: FulfillResponseEnum.Pending },
              status: 200,
              statusText: "Gateway timeout",
              headers: [],
              config: {},
            });
          }
          return Promise.reject(e);
        });
      switch (finalizedCheckedResponse.FulfillResponse) {
        case FulfillResponseEnum.Failure: {
          pollFailed = true;
          const failedResponse = finalizedCheckedResponse as FulfillFailure;
          const updatedState: IStoreState = yield select();

          const completeBuyHotelProperties =
            getCompleteBuyHotelProperties(updatedState);
          const cardDeclineProps = getCardDeclineProps(failedResponse.errors);
          trackEvent({
            eventName: COMPLETE_BUY_HOTEL,
            properties: {
              ...completeBuyHotelProperties.properties,
              ...cardDeclineProps,
              success: false,
              agent_booking_fee_amount_usd: agentFee,
              failure_reason:
                failedResponse.errors.length > 0
                  ? failedResponse.errors
                      .map((error) => toErrorString(error))
                      .join(" - ")
                  : "Poll Confirmation Details response returned an error and the given error code is not handleable.",
              is_recommended: isRecommended,
              purpose_of_travel: tripPurpose,
              booking_duration: dayjs().diff(initialTime, "seconds", true),
            },
            encryptedProperties: [
              ...completeBuyHotelProperties.encryptedProperties,
            ],
          });
          if (failedResponse.errors.length > 0) {
            yield putResolve(
              setPollConfirmationDetailsCallStateFailure(failedResponse.errors)
            );
            return;
          }
          yield putResolve(setPollConfirmationDetailsCallStateFailure([]));
          throw new Error(
            "Poll Confirmation Details response returned an error and the given error code is not handleable."
          );
        }

        case FulfillResponseEnum.Pending:
          break;
        case FulfillResponseEnum.Success: {
          const finalizedResponse =
            finalizedCheckedResponse as FulfillSuccessV0;
          const reservation = finalizedResponse.fulfillment
            .value as Reservation;
          if (reservation) {
            yield putResolve(
              setConfirmationDetails({
                confirmationDetails: reservation,
                type: "hotel-checkout",
              })
            );
            yield putResolve(setPollConfirmationDetailsCallStateSuccess());

            if (getIsBrexApp() && reservation.reservationId) {
              brexBridge.emit("BOOKING_COMPLETE", {
                reservationId: reservation.reservationId,
              });
            }

            // Tysons' deprecated plain `trackingProperties` (not on the Reservation type); V2 is encrypted.
            const refundableAdvance =
              finalizedResponse?.fulfillment?.value?.trackingProperties
                ?.refundable_advance ?? undefined;
            const cancellationPolicy =
              finalizedResponse?.fulfillment?.value?.trackingProperties
                ?.cancellation_policy ?? undefined;

            const successState: IStoreState = yield select();

            const successCompleteBuyHotelProperties =
              getCompleteBuyHotelProperties(successState);

            const hotelCfarPremiumProperties =
              getHotelCfarPremiumProperties(successState);
            trackEvent({
              eventName: COMPLETE_BUY_HOTEL,
              properties: {
                ...successCompleteBuyHotelProperties.properties,
                ...hotelCfarPremiumProperties,
                success: true,
                agent_booking_fee_amount_usd: agentFee,
                reservation_id: reservation.reservationId,
                is_recommended: isRecommended,
                refundable_advance: refundableAdvance,
                cancellation_policy: cancellationPolicy,
                hotel_cancellation_window: getFreeCancellationDeadline(
                  reservation.cancellationPolicyV2
                ),
                refundable_rate_premium:
                  getHotelRefundableRatePremiumSelector(successState),
                purpose_of_travel: tripPurpose,
                booking_duration: dayjs().diff(initialTime, "seconds", true),
              },
              encryptedProperties: [
                ...successCompleteBuyHotelProperties.encryptedProperties,
              ],
            });
            const hotelBookingData = buildHotelBookingData(reservation);
            travelAppInterface.sendEvent({
              name: TravelAppInterfaceEventName.BOOKING_COMPLETE,
              funnel: TravelAppInterfaceFunnelType.HOTELS,
              data: hotelBookingData,
            });
            return;
          }
          pollFailed = true;
          const failedState: IStoreState = yield select();
          const failedCompleteBuyHotelProperties =
            getCompleteBuyHotelProperties(failedState);
          trackEvent({
            eventName: COMPLETE_BUY_HOTEL,
            properties: {
              ...failedCompleteBuyHotelProperties.properties,
              success: false,
              agent_booking_fee_amount_usd: agentFee,
              failure_reason:
                "Poll Confirmation Details Success Response Failed",
              is_recommended: isRecommended,
              purpose_of_travel: tripPurpose,
              booking_duration: dayjs().diff(initialTime, "seconds", true),
            },
            encryptedProperties: [
              ...failedCompleteBuyHotelProperties.encryptedProperties,
            ],
          });
          yield putResolve(setPollConfirmationDetailsCallStateFailure([]));
          throw new Error("Poll Finalized Failed");
        }
        default:
          break;
      }

      // if we want to give up on polling we should instead direct the user to my trips
      if (index >= delayTimes.length) {
        window.location.pathname = "/trips";
      }

      if (index !== delayTimes.length - 1) {
        index++;
      }
    }
  } catch {
    const state: IStoreState = yield select();
    const tripPurpose = getTripPurpose(state);
    const completeBuyHotelProperties = getCompleteBuyHotelProperties(state);

    trackEvent({
      eventName: COMPLETE_BUY_HOTEL,
      properties: {
        ...completeBuyHotelProperties.properties,
        success: false,
        agent_booking_fee_amount_usd: agentFee,
        failure_reason: "Poll Confirmation Details Network Call Failed",
        is_recommended: isRecommended,
        purpose_of_travel: tripPurpose,
      },
      encryptedProperties: [...completeBuyHotelProperties.encryptedProperties],
    });
    yield putResolve(setPollConfirmationDetailsCallStateFailure([]));
  }
}
